import { Layout } from "antd";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useParams } from "react-router";

import HeaderButtons from "@/components/Header/Asset";
import BaseHeader from "@/components/Header/BaseHeader";
import AssetsTable from "@/components/ListTable/AssetsTable";
import InterventionsTable from "@/components/ListTable/InterventionsTable";
import ObservationsTable from "@/components/ListTable/ObservationsTable";
import { STORAGE_KEYS } from "@/constants/StorageKeys";
import { TAB_KEYS } from "@/constants/TabKeys";
import { useGetAsset } from "@/entities/asset";
import { ASSETS_FILTERS } from "@/entities/assets";
import {
  INTERVENTIONS_FILTERS,
  INTERVENTIONS_INCLUDES,
} from "@/entities/interventions";
import { OBSERVATIONS_FILTERS } from "@/entities/observations";
import AssetAddButton from "@/modules/asset/asset-add-button";
import AssetBreadcrumb from "@/modules/asset/asset-breadcrumb";
import { AssetIndicators } from "@/modules/asset/asset-indicators";
import { AssetPageMap } from "@/modules/asset/asset-page";
import AssetTechnicalTab from "@/modules/asset/asset-technical-tab";
import { AssetTimeline } from "@/modules/asset/asset-timeline";
import InterventionAddButton from "@/modules/intervention/intervention-add-button";
import { ListProvider } from "@/modules/list";
import { isError404 } from "@/services/api";
import { ContextTabs } from "@/ui/context-tabs";

const { Content } = Layout;

const Details = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();

  const defaultTab = location.pathname.includes(TAB_KEYS.ASSETS_OBSERVATIONS)
    ? TAB_KEYS.ASSETS_OBSERVATIONS
    : TAB_KEYS.ASSETS_TECHNICAL;

  const { isLoading, asset = {}, error } = useGetAsset(id);

  const tabs = [
    {
      key: TAB_KEYS.ASSETS_TECHNICAL,
      label: t("asset:technical.label"),
      children: asset ? <AssetTechnicalTab asset={asset} /> : <></>,
    },
    {
      key: TAB_KEYS.ASSETS_COMPONENTS,
      label: t("asset:components.label"),
      children: (
        <ListProvider
          model="assets"
          storageKey={STORAGE_KEYS.ASSETS_COMPONENTS}
          contextTabValue={TAB_KEYS.ASSETS_COMPONENTS}
          hiddenFilters={{ [ASSETS_FILTERS.ASSET_ID]: id }}
        >
          <AssetsTable
            extraActions={
              <AssetAddButton
                ancestors={asset.ancestors}
                defaultCoordinate={asset.coordinate}
                shortButtonText
                sidePanelTitle={t("asset:form.add.component")}
                loading={isLoading}
              />
            }
          />
        </ListProvider>
      ),
    },
    {
      key: TAB_KEYS.ASSETS_INTERVENTIONS,
      label: t("asset:interventions.label"),
      children: (
        <ListProvider
          model="interventions"
          storageKey={STORAGE_KEYS.ASSETS_INTERVENTIONS}
          contextTabValue={TAB_KEYS.ASSETS_INTERVENTIONS}
          includes={[INTERVENTIONS_INCLUDES.USERS]}
          hiddenFilters={{ [INTERVENTIONS_FILTERS.ASSET_ID]: id }}
        >
          <InterventionsTable
            extraActions={
              <InterventionAddButton
                ancestors={asset.ancestors}
                shortButtonText
                loading={isLoading}
              />
            }
          />
        </ListProvider>
      ),
    },
    {
      key: TAB_KEYS.ASSETS_OBSERVATIONS,
      label: t("asset:observations.label"),
      children: (
        <ListProvider
          model="observations"
          storageKey={STORAGE_KEYS.ASSETS_OBSERVATIONS}
          contextTabValue={TAB_KEYS.ASSETS_OBSERVATIONS}
          hiddenFilters={{
            [OBSERVATIONS_FILTERS.CATEGORY]: "defect",
            [OBSERVATIONS_FILTERS.ASSET_ID]: id,
          }}
          defaultSort={["-risk_level", "state"]}
        >
          <ObservationsTable
            hiddenColumns={["asset.id", "asset.asset_type_id"]}
          />
        </ListProvider>
      ),
    },
    {
      key: TAB_KEYS.ASSETS_TIMELINE,
      label: t("asset:timeline.label"),
      children: <AssetTimeline assetId={id} />,
    },
  ];

  if (isError404(error)) {
    return <Navigate to="/assets" />;
  }

  return (
    <>
      <div className="base-header-wrapper">
        <AssetPageMap asset={asset} isLoading={isLoading} />
        <BaseHeader
          title={asset?.name}
          afterTitle={<AssetIndicators asset={asset} />}
          breadcrumb={<AssetBreadcrumb asset={asset} />}
          loading={isLoading}
          headerClass="header--absolute"
          extra={
            !isLoading && [<HeaderButtons key="HeaderButtons" asset={asset} />]
          }
        />
      </div>
      <Content>
        <ContextTabs defaultActiveKey={defaultTab} items={tabs} />
      </Content>
    </>
  );
};

export default Details;
